import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getServizi, deleteServizio, deleteMultipleServizi, getSuggerimentiCategorieServizi, getSuggerimentiSupportiServizi } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, InputGroup, OverlayTrigger, Popover, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import ActionToolbar from '../common/ActionToolbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faSortUp, faSortDown, faTimes, faCog, faBoxes, faEuroSign, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import FilterPanel from '../common/FilterPanel';
import SortController from '../common/SortController';
import ColumnOrderController from '../common/ColumnOrderController';
import SortableHeader from '../common/SortableHeader';
import StatsCards from '../common/StatsCards';
import { usePageTitle } from '../context/PageTitleContext';
import { getOptimalColumnWidth, getMinColumnWidth, autoFitColumns, autoResizeColumn, resetColumnWidths } from '../common/columnSizingUtils';

const columnHelper = createColumnHelper();

// Colonne numeriche per i filtri avanzati
const numericColumns = ['id', 'prezzo', 'iva', 'costo', 'prezzo_indicativo_da', 'costo_unitario_base'];

// Colonne data per i filtri avanzati
const dateColumns = ['data_creazione', 'data_aggiornamento'];

const allColumnsMap = {
  id: "ID",
  sku: "SKU",
  titolo_servizio: "Titolo Servizio", // Nuovo campo
  supporto: "Supporto", // Aggiunto
  descrizione: "Descrizione",
  immagine_url: "URL Immagine", // Nuovo campo
  prezzo: "Prezzo",
  iva: "IVA",
  costo: "Costo",
  categoria: "Categoria",
  prezzo_indicativo_da: "Prezzo Indicativo Da", // Nuovo campo
  unita_misura_base: "Unità Misura Base", // Nuovo campo
  costo_unitario_base: "Costo Unitario Base", // Nuovo campo
  opzioni_configurabili_json: "Opzioni Configurabili (JSON)", // Nuovo campo
  attivo: "Attivo", // Nuovo campo
  pubblica_su_web: "Pubblica su Web",
  data_creazione: "Data Creazione", // Nuovo campo
  data_aggiornamento: "Data Aggiornamento", // Nuovo campo
};

const initialVisibleColumns = ['id', 'titolo_servizio', 'sku', 'supporto', 'prezzo', 'attivo', 'pubblica_su_web'];


const visibilityKey = 'table-visibility-servizi';

function ServiziList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedServiziIds, setSelectedServiziIds] = useState([]);
  const [filterPanelKey, setFilterPanelKey] = useState(0);
  const [distinctValues, setDistinctValues] = useState({});

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-servizi'); // Modificato
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-servizi'); // Modificato
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-servizi');
    const allColumnIds = Object.keys(allColumnsMap);
    const defaultOrder = ['select', ...allColumnIds];

    if (savedOrder) {
        try {
            let parsedOrder = JSON.parse(savedOrder);
            // Filtra solo le colonne che esistono ancora in allColumnsMap
            let validOrder = parsedOrder.filter(id => allColumnIds.includes(id) || id === 'select');
            
            // Aggiungi eventuali nuove colonne da allColumnsMap che non sono nell'ordine salvato
            allColumnIds.forEach(id => {
                if (!validOrder.includes(id)) {
                    validOrder.push(id);
                }
            });
            return validOrder;
        } catch (e) {
            console.error("Errore nel parsing di columnOrder da localStorage", e);
            return defaultOrder;
        }
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-servizi'); // Modificato
    return saved ? JSON.parse(saved) : {};
  });

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const savedPage = sessionStorage.getItem('list-currentPage-servizi'); // Modificato
      return savedPage ? JSON.parse(savedPage) : 1;
    } catch (e) {
      console.error("Errore durante il parsing di currentPage da sessionStorage", e);
      return 1;
    }
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const savedItemsPerPage = sessionStorage.getItem('list-itemsPerPage-servizi'); // Modificato
      return savedItemsPerPage ? JSON.parse(savedItemsPerPage) : 100;
    } catch (e) {
      console.error("Errore durante il parsing di itemsPerPage da sessionStorage", e);
      return 100;
    }
  });
  const [totalServizi, setTotalServizi] = useState(0); // Modificato

  // Sticky columns configuration
  const stickyColumns = ['select'];

  const fetchServiziData = useCallback(async () => { // Modificato
    setLoading(true);
    setError(null);
    try {
      const filters = columnFilters.reduce((acc, filter) => {
            const { id, value } = filter;
            
            // Range filter for numbers/dates (array with [min, max])
            if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
              const [min, max] = value;
              
              if (min) {
                acc[`${id}__gte`] = numericColumns.includes(id) ? parseFloat(min) : min;
              }
              if (max) {
                acc[`${id}__lte`] = numericColumns.includes(id) ? parseFloat(max) : max;
              }
            }
            // Multi-select filter
            else if (Array.isArray(value)) {
                if (value.length > 0) {
                    acc[id] = { 'in': value };
                }
            } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
                acc[id] = null;
            } else if (value !== undefined && value !== '') {
                acc[id] = value;
            }
            return acc;
        }, {});

      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const stringifiedFilters = JSON.stringify(filters);

      const response = await getServizi((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, stringifiedFilters); // Modificato
      setServizi(response.data.servizi); // Modificato
      setTotalServizi(response.data.total); // Modificato
      // Non ci sono facet per i servizi al momento, si possono aggiungere in futuro
      // if (response.data.facets) {
      //   setDistinctValues(response.data.facets);
      // }
    } catch (err) {
      setError('Errore nel caricamento dei servizi.'); // Modificato
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  const handleResetFilters = useCallback(() => {
    setColumnFilters([]);
    setCurrentPage(1);
    setSorting([]);
    setFilterPanelKey(k => k + 1);
    sessionStorage.removeItem('list-sorting-servizi');
    sessionStorage.removeItem('list-filters-servizi');
    sessionStorage.removeItem('list-currentPage-servizi');
    sessionStorage.removeItem('list-itemsPerPage-servizi');
  }, [setColumnFilters, setCurrentPage, setSorting]);

  useEffect(() => {
    const fetchFilterData = async () => {
        try {
            const [categorieRes, supportiRes] = await Promise.all([
                getSuggerimentiCategorieServizi(),
                getSuggerimentiSupportiServizi(),
            ]);
            setDistinctValues({
                categoria: categorieRes.data,
                supporto: supportiRes.data,
            });
        } catch (error) {
            console.error("Errore nel caricamento dei dati per i filtri:", error);
        }
    };
    fetchFilterData();
    fetchServiziData();
  }, [fetchServiziData]);

  // Rimozione useEffect per layout di stampa e reportType in quanto non applicabili ai servizi
  // useEffect per layout di stampa
  // useEffect(() => {
  //   const fetchLayouts = async () => {
  //       try {
  //           const response = await getLayoutsByModulo('Servizio');
  //           setPrintLayouts(response.data);
  //       } catch (error) {
  //           console.error("Errore nel caricamento dei layout di stampa", error);
  //       }
  //   };
  //   fetchLayouts();
  // }, []);

  const getColumnValue = (servizio, columnName) => { // Modificato
    switch (columnName) {
      case 'data_creazione':
      case 'data_aggiornamento':
        return servizio[columnName] ? new Date(servizio[columnName]).toLocaleString() : 'N/A';
      case 'attivo':
        return servizio[columnName] ? 'Sì' : 'No';
      case 'pubblica_su_web':
        return (
          <Badge bg={servizio[columnName] ? 'success' : 'danger'}>
            {servizio[columnName] ? 'Sì' : 'No'}
          </Badge>
        );
      case 'opzioni_configurabili_json':
        try {
          const jsonVal = JSON.parse(servizio[columnName]);
          return JSON.stringify(jsonVal); // Mostra il JSON formattato come stringa
        } catch {
          return servizio[columnName] || '[]'; // Se non è JSON valido, mostra la stringa originale
        }
      default:
        return servizio[columnName] != null ? String(servizio[columnName]) : 'N/A';
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={servizi.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => {
            const servizio = info.row.original;
            if (key === 'titolo_servizio') {
              const imagePopover = (
                <Popover id={`popover-image-${servizio.id}`} style={{ maxWidth: '250px' }}>
                  <Popover.Body>
                    {servizio.immagine_url ? (
                      <img 
                        src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${servizio.immagine_url}`} 
                        alt={servizio.titolo_servizio} 
                        className="img-fluid" 
                      />
                    ) : (
                      'Nessuna immagine'
                    )}
                  </Popover.Body>
                </Popover>
              );
              return (
                <OverlayTrigger placement="right" overlay={imagePopover} delay={{ show: 250, hide: 400 }}>
                  <span>{getColumnValue(servizio, key)}</span>
                </OverlayTrigger>
              );
            }
            return getColumnValue(servizio, key);
          },
          enableColumnFilter: true,
          meta: {
            filterVariant: ['attivo', 'pubblica_su_web', 'categoria', 'supporto'].includes(key) ? 'select' : 'text',
            filterOptions: 
              ['attivo', 'pubblica_su_web'].includes(key) 
              ? [{value: true, label: 'Sì'}, {value: false, label: 'No'}] 
              : (['categoria', 'supporto'].includes(key) && distinctValues[key] 
                  ? distinctValues[key].map(val => ({ value: val, label: val }))
                  : undefined),
          },
        })
      ),
    ],
    [servizi, distinctValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: servizi, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing }, // Modificato
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true,
  });

  const handleAutoResize = (column) => {
    autoResizeColumn(column, table);
  };

  const handleAutoFitAllColumns = () => {
    autoFitColumns(table);
  };

  const handleResetColumnWidths = () => {
    resetColumnWidths(table);
  };

  // Calcola statistiche per StatsCards
  const stats = useMemo(() => {
    const totalValue = servizi.reduce((sum, item) => sum + ((item.prezzo || 0)), 0);
    const activeServices = servizi.filter(item => item.attivo).length;
    
    return [
      { label: 'Totale Servizi', value: totalServizi, icon: faCog, color: 'primary' },
      { label: 'Servizi Attivi', value: activeServices, icon: faBoxes, color: 'success' },
      { label: 'Valore Totale', value: `€ ${totalValue.toFixed(2)}`, icon: faEuroSign, color: 'warning' },
    ];
  }, [servizi, totalServizi]);

  const activeFiltersCount = useMemo(() => {
    return columnFilters.filter(f => {
      if (f.id === 'attivo') return false;
      if (Array.isArray(f.value)) return f.value.length > 0;
      return f.value !== undefined && f.value !== '';
    }).length;
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-sorting-servizi', JSON.stringify(sorting)); // Modificato
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-servizi', JSON.stringify(columnFilters)); // Modificato
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-currentPage-servizi', JSON.stringify(currentPage)); // Modificato
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem('list-itemsPerPage-servizi', JSON.stringify(itemsPerPage)); // Modificato
  }, [itemsPerPage]);

  useEffect(() => {
    localStorage.setItem('table-column-order-servizi', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-servizi', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);

    setSelectedServiziIds(newSelectedIds); // Modificato
  }, [rows]);

  const handleRowClick = useCallback((row) => {
    sessionStorage.setItem('list-currentPage-servizi', JSON.stringify(currentPage)); // Modificato
    sessionStorage.setItem('list-itemsPerPage-servizi', JSON.stringify(itemsPerPage)); // Modificato
    sessionStorage.setItem('list-sorting-servizi', JSON.stringify(sorting)); // Modificato
    sessionStorage.setItem('list-filters-servizi', JSON.stringify(columnFilters)); // Modificato
    
    navigate(`/servizi/${row.original.id}`); // Modificato
  }, [currentPage, itemsPerPage, sorting, columnFilters, navigate]);

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalServizi / itemsPerPage);
    const paginationItems = [];
    const maxPagesToShow = 5; // Numero massimo di pagine da mostrare

    // Determinare le pagine iniziali e finali da mostrare
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Se stiamo mostrando meno di maxPagesToShow pagine e siamo verso la fine, aggiusta startPage
    if (endPage - startPage + 1 < maxPagesToShow && totalPages > maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      paginationItems.push(<Pagination.Item key={1} onClick={() => setCurrentPage(1)}>{1}</Pagination.Item>);
      if (startPage > 2) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      paginationItems.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      paginationItems.push(<Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>);
    }

    return paginationItems;
  };



  // Funzioni export/import rimosse
  const deleteApiMap = { servizi: deleteServizio }; // Modificato
  const deleteMultipleApiMap = { servizi: deleteMultipleServizi }; // Modificato

  // Usiamo SortableHeader dal componente comune invece di definirlo localmente

  const hasManualFilters = table.getState().columnFilters.length > 0;

  // Funzione per renderizzare il contenuto della card mobile
  const renderServizioCard = useCallback((servizio, isSelected, onToggleSelect) => {
    return (
      <Row className="align-items-start">
        {/* Checkbox Column */}
        <Col xs={2} className="d-flex align-items-center justify-content-center">
          <Form.Check 
            type="checkbox" 
            checked={isSelected} 
            onChange={onToggleSelect}
            style={{ transform: 'scale(1.3)' }}
          />
        </Col>

        {/* Image Column */}
        <Col xs={3} className="d-flex align-items-center justify-content-center">
          {servizio.immagine_url ? (
            <img 
              src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${servizio.immagine_url}`}
              alt={servizio.titolo_servizio}
              style={{ 
                width: '100%', 
                maxWidth: '70px',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div style={{ 
              width: '70px', 
              height: '70px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              color: '#6c757d',
              textAlign: 'center'
            }}>
              <FontAwesomeIcon icon={faCog} size="2x" />
            </div>
          )}
        </Col>

        {/* Info Column */}
        <Col xs={7}>
          <div className="mb-1">
            <strong style={{ fontSize: '0.95rem', color: '#212529' }}>
              {servizio.titolo_servizio}
            </strong>
          </div>
          
          {servizio.sku && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '4px' }}>
              <strong>SKU:</strong> {servizio.sku}
            </div>
          )}
          
          {servizio.supporto && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '4px' }}>
              <strong>Supporto:</strong> {servizio.supporto}
            </div>
          )}

          {servizio.categoria && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '6px' }}>
              <strong>Categoria:</strong> {servizio.categoria}
            </div>
          )}

          <div className="d-flex flex-wrap" style={{ gap: '4px', marginTop: '8px' }}>
            {servizio.prezzo != null && (
              <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                € {parseFloat(servizio.prezzo).toFixed(2)}
              </Badge>
            )}
            
            <Badge bg={servizio.attivo ? 'primary' : 'secondary'} style={{ fontSize: '0.65rem' }}>
              {servizio.attivo ? 'Attivo' : 'Non Attivo'}
            </Badge>
            
            <Badge bg={servizio.pubblica_su_web ? 'info' : 'warning'} style={{ fontSize: '0.65rem' }}>
              {servizio.pubblica_su_web ? 'Web' : 'No Web'}
            </Badge>
          </div>
        </Col>
      </Row>
    );
  }, []);

  // Configurazione filtri rapidi per mobile
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faCog,
      isActive: !columnFilters.find(f => f.id === 'attivo'),
      onClick: () => {
        setColumnFilters(prev => prev.filter(f => f.id !== 'attivo'));
      },
      variant: 'outline-secondary',
      gradientActive: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      color: '#6c757d',
      borderColorActive: '#007bff'
    },
    {
      label: 'Attivi',
      icon: faBoxes,
      isActive: columnFilters.find(f => f.id === 'attivo')?.value === true,
      onClick: () => {
        setColumnFilters(prev => {
          const filtered = prev.filter(f => f.id !== 'attivo');
          return [...filtered, { id: 'attivo', value: true }];
        });
      },
      variant: 'outline-success',
      gradientActive: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
      color: '#28a745',
      borderColorActive: '#28a745'
    },
    {
      label: 'Non Attivi',
      icon: faTimes,
      isActive: columnFilters.find(f => f.id === 'attivo')?.value === false,
      onClick: () => {
        setColumnFilters(prev => {
          const filtered = prev.filter(f => f.id !== 'attivo');
          return [...filtered, { id: 'attivo', value: false }];
        });
      },
      variant: 'outline-danger',
      gradientActive: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
      color: '#dc3545',
      borderColorActive: '#dc3545'
    }
  ], [columnFilters]);

  usePageTitle('Servizi');

  return (
    <>
      <StatsCards stats={stats} />

    <Card>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          // Mobile Layout con componente riutilizzabile
          <MobileListLayout
            entityName="servizio"
            entityNamePlural="servizi"
            createPath="/servizi/new"
            createButtonText="Nuovo Servizio"
            createButtonIcon={faPlus}
            actionsDropdownContent={
              <>
                {/* Azioni disponibili */}
              </>
            }
            quickFilters={quickFilters}
            searchPlaceholder="Ricerca servizi..."
            activeFiltersCount={activeFiltersCount}
            onSearchChange={(e) => {
              const val = e.target.value;
              setColumnFilters(prev => {
                const without = prev.filter(f => f.id !== '_global_search');
                return val ? [...without, { id: '_global_search', value: val }] : without;
              });
              setCurrentPage(1);
            }}
            searchValue={columnFilters.find(f => f.id === '_global_search')?.value || ''}
            items={table.getRowModel().rows.map(row => row.original)}
            selectedIds={selectedServiziIds}
            renderCard={renderServizioCard}
            onCardClick={(servizio) => navigate(`/servizi/${servizio.id}`)}
            onToggleSelect={(id) => {
              const row = table.getRowModel().rows.find(r => r.original.id === id);
              if (row) row.toggleSelected();
            }}
            onDeleteMultiple={async (ids) => {
              await deleteMultipleServizi(ids);
              fetchServiziData();
              table.resetRowSelection();
            }}
            deleteConfirmMessage={(count) => `Eliminare ${count} servizi?`}
          />
        ) : (
          // Desktop Layout
        <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/servizi/new" className="me-2" size="sm" variant="success">Nuovo Servizio</Button>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
              <Dropdown.Menu>
                {/* Azioni stampa rimosse per ora */}
              </Dropdown.Menu>
            </Dropdown>

          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">Ordinamento</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-2">
                <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters}>Azzera Filtri</Button>
            <Button variant="outline-primary" size="sm" className="ms-2" onClick={handleAutoFitAllColumns}>Auto-Fit</Button>
            <Button variant="outline-warning" size="sm" className="ms-2" onClick={handleResetColumnWidths}>Reset Colonne</Button>
          </Col>
        </Row>
        )}
      </Card.Header>
      <Card.Body>
        <FilterPanel
          key={filterPanelKey}
          fields={[
            { id: '_global_search', label: 'Cerca', type: 'text', placeholder: 'Nome, descrizione...', colSize: 4 },
            { id: 'categoria', label: 'Categoria', type: 'select', options: distinctValues.categoria || [], colSize: 3 },
            { id: 'attivo', label: 'Attivo', type: 'select', options: ['true', 'false'], colSize: 2 },
          ]}
          onApply={(filterValues) => {
            const newColumnFilters = Object.entries(filterValues)
              .filter(([, v]) => v !== undefined && v !== '' && v !== null)
              .map(([id, value]) => ({ id, value }));
            setColumnFilters(newColumnFilters);
            setCurrentPage(1);
          }}
        />
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !isMobile && (
          <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
            <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map(header => (
                        <SortableHeader 
                          key={header.id} 
                          header={header} 
                          table={table} 
                          handleAutoResize={handleAutoResize}
                          stickyColumns={stickyColumns}
                        />
                      ))}
                    </tr>

                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedServiziIds} entityType="servizi" onDeleteSuccess={fetchServiziData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} />
        )}
      </Card.Body>
      {!isMobile && (
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalServizi / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalServizi / itemsPerPage))} disabled={currentPage === Math.ceil(totalServizi / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
      )}
    </Card>
  </>
  );
}

export default ServiziList; // Modificato
